// ==============================================
// Admin Fleet Map Page
// ==============================================

import { useState, useMemo, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  MapPinIcon,
  XMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { api, type FleetMapMarker } from '../../services/api';
import { AdminFleetMap } from '../../components/admin/console';
import { formatDistanceToNow } from 'date-fns';

// ---- Role definitions ----

type UserRole = 'user' | 'tester' | 'tester_plus' | 'admin' | 'super_admin';

const ROLE_LABELS: Record<UserRole, string> = {
  user: 'User',
  tester: 'Tester',
  tester_plus: 'Tester+',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

const ALL_ROLES: UserRole[] = ['user', 'tester', 'tester_plus', 'admin', 'super_admin'];

// ---- Pin colour by device status + last-seen age ----

type PinColour = 'green' | 'amber' | 'red' | 'grey';

function getPinColour(marker: FleetMapMarker): PinColour {
  if (!marker.userId || marker.deviceStatus === 'suspended' || marker.deviceStatus === 'revoked') return 'grey';
  if (!marker.lastSeenAt) return 'grey';

  const minsAgo = (Date.now() - new Date(marker.lastSeenAt).getTime()) / 60000;
  if (minsAgo < 60) return 'green';
  if (minsAgo < 1440) return 'amber';
  return 'red';
}

const PIN_COLOURS: Record<PinColour, string> = {
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  grey: '#6b7280',
};

function makeIcon(colour: PinColour) {
  const hex = PIN_COLOURS[colour];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24S24 21 24 12C24 5.4 18.6 0 12 0z" fill="${hex}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white" opacity="0.9"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -36],
  });
}

// ---- Legend ----

const LEGEND_ITEMS: { colour: PinColour; label: string }[] = [
  { colour: 'green', label: 'Online (< 1 hr)' },
  { colour: 'amber', label: 'Recent (1–24 hr)' },
  { colour: 'red', label: 'Stale (> 24 hr)' },
  { colour: 'grey', label: 'No subscription / offline' },
];

// ---- Map controller (imperative flyTo) ----

interface MapControllerHandle {
  flyTo: (lat: number, lon: number, zoom?: number) => void;
}

const MapController = forwardRef<MapControllerHandle>((_props, ref) => {
  const map = useMap();
  useImperativeHandle(ref, () => ({
    flyTo: (lat: number, lon: number, zoom = 12) => {
      map.flyTo([lat, lon], zoom, { duration: 1.2 });
    },
  }));
  return null;
});

// ---- Marker info panel ----

function MarkerPanel({ marker, onClose }: { marker: FleetMapMarker; onClose: () => void }) {
  const colour = getPinColour(marker);
  const age = marker.lastSeenAt
    ? formatDistanceToNow(new Date(marker.lastSeenAt), { addSuffix: true })
    : 'Never';

  return (
    <div className="absolute top-4 right-4 z-[1000] w-72 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5"
            style={{ backgroundColor: PIN_COLOURS[colour] }}
          />
          <div>
            <p className="font-semibold text-white text-sm leading-tight">
              {marker.userName || marker.userEmail || 'Unregistered'}
            </p>
            {marker.userName && (
              <p className="text-xs text-slate-400">{marker.userEmail}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          className="text-slate-400 hover:text-white ml-2"
          onClick={onClose}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      <dl className="space-y-1.5 text-xs">
        <div className="flex justify-between">
          <dt className="text-slate-400">Device</dt>
          <dd className="text-white text-right">{marker.deviceName || marker.deviceId}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-400">Type</dt>
          <dd className="text-white capitalize">{marker.deviceType.replace('_', ' ')}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-400">Status</dt>
          <dd className="text-white capitalize">{marker.deviceStatus}</dd>
        </div>
        {marker.userRole && (
          <div className="flex justify-between">
            <dt className="text-slate-400">Role</dt>
            <dd className="text-white">
              {ROLE_LABELS[marker.userRole as UserRole] ?? marker.userRole}
            </dd>
          </div>
        )}
        {marker.packageName && (
          <div className="flex justify-between">
            <dt className="text-slate-400">Plan</dt>
            <dd className="text-white">{marker.packageName}</dd>
          </div>
        )}
        <div className="flex justify-between">
          <dt className="text-slate-400">Last seen</dt>
          <dd className="text-white">{age}</dd>
        </div>
        {marker.lastKnownPosition && (
          <div className="flex justify-between">
            <dt className="text-slate-400">Position</dt>
            <dd className="text-white font-mono text-right">
              {marker.lastKnownPosition.lat.toFixed(4)}°{' '}
              {marker.lastKnownPosition.lon.toFixed(4)}°
            </dd>
          </div>
        )}
      </dl>

      <Link
        to={`/admin/devices/${marker.deviceId}`}
        className="mt-3 block w-full text-center text-xs px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
      >
        View device
      </Link>
    </div>
  );
}

// ---- Role dropdown pill ----

function RoleDropdown({
  label,
  count,
  active,
  onToggle,
  items,
  onSelect,
}: {
  label: string;
  count: number;
  active: boolean;
  onToggle: () => void;
  items: FleetMapMarker[];
  onSelect: (marker: FleetMapMarker) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        const nameA = a.userName || a.userEmail || a.deviceName || a.deviceId;
        const nameB = b.userName || b.userEmail || b.deviceName || b.deviceId;
        return nameA.localeCompare(nameB);
      }),
    [items],
  );

  return (
    <div className="relative" ref={ref}>
      <div className="flex">
        {/* Left part: toggle filter */}
        <button
          type="button"
          onClick={onToggle}
          className={`flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-l-lg text-xs font-medium transition-colors border border-r-0 ${
            active
              ? 'bg-red-500/20 border-red-500/50 text-red-300'
              : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white'
          }`}
        >
          {label}
          <span className={`text-xs ${active ? 'text-red-400' : 'text-slate-500'}`}>
            {count}
          </span>
        </button>
        {/* Right part: open dropdown */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`flex items-center px-1.5 py-1.5 rounded-r-lg text-xs transition-colors border border-l-0 ${
            active
              ? 'bg-red-500/20 border-red-500/50 text-red-300'
              : open
                ? 'bg-slate-700 border-slate-600 text-white'
                : 'bg-slate-800 border-slate-700 text-slate-500 hover:bg-slate-700 hover:text-white'
          }`}
        >
          <ChevronDownIcon className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-[1200] w-64 max-h-60 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg shadow-2xl py-1">
          {sorted.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">No devices</p>
          ) : (
            sorted.map((m) => {
              const hasPosition = !!m.lastKnownPosition;
              const colour = getPinColour(m);
              return (
                <button
                  key={m.deviceId}
                  type="button"
                  disabled={!hasPosition}
                  onClick={() => {
                    onSelect(m);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-xs transition-colors ${
                    hasPosition
                      ? 'hover:bg-slate-700 cursor-pointer'
                      : 'opacity-50 cursor-default'
                  }`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: hasPosition ? PIN_COLOURS[colour] : '#374151' }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-white truncate">
                      {m.userName || m.userEmail || 'Unregistered'}
                    </p>
                    {m.deviceName && (
                      <p className="text-slate-500 truncate">{m.deviceName}</p>
                    )}
                  </div>
                  {!hasPosition && (
                    <span className="text-[10px] text-slate-600 flex-shrink-0">No GNSS</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ---- No GNSS dropdown (info only, no filter toggle) ----

function NoGnssDropdown({
  items,
}: {
  items: FleetMapMarker[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        const nameA = a.userName || a.userEmail || a.deviceName || a.deviceId;
        const nameB = b.userName || b.userEmail || b.deviceName || b.deviceId;
        return nameA.localeCompare(nameB);
      }),
    [items],
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
          open
            ? 'bg-slate-700 border-slate-600 text-white'
            : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white'
        }`}
      >
        No GNSS
        <span className="text-slate-500">{items.length}</span>
        <ChevronDownIcon className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-[1200] w-64 max-h-60 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg shadow-2xl py-1">
          {sorted.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">No devices</p>
          ) : (
            sorted.map((m) => (
              <div
                key={m.deviceId}
                className="w-full text-left px-3 py-1.5 flex items-center gap-2 text-xs"
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0 bg-slate-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-slate-300 truncate">
                    {m.userName || m.userEmail || 'Unregistered'}
                  </p>
                  {m.deviceName && (
                    <p className="text-slate-500 truncate">{m.deviceName}</p>
                  )}
                </div>
                <span className="text-[10px] text-slate-600 flex-shrink-0">
                  {ROLE_LABELS[m.userRole as UserRole] ?? m.userRole ?? 'N/A'}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---- Main Page ----

export function AdminFleetMapPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PinColour | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<Set<UserRole | 'unregistered'>>(new Set());
  const [selectedMarker, setSelectedMarker] = useState<FleetMapMarker | null>(null);
  const mapRef = useRef<MapControllerHandle>(null);

  const { data, isLoading, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'fleet-map'],
    queryFn: () => api.adminGetFleetMapMarkers(),
    refetchInterval: 60_000,
  });

  const allDevices = data ?? [];

  const markers = useMemo(() => allDevices.filter((m) => m.lastKnownPosition), [allDevices]);
  const noGnssDevices = useMemo(() => allDevices.filter((m) => !m.lastKnownPosition && m.userId), [allDevices]);

  // Group devices by role for the dropdowns (includes all devices, with/without position)
  const devicesByRole = useMemo(() => {
    const groups: Record<UserRole | 'unregistered', FleetMapMarker[]> = {
      user: [], tester: [], tester_plus: [], admin: [], super_admin: [], unregistered: [],
    };
    // Include positioned markers in role groups
    markers.forEach((m) => {
      const role = (m.userRole as UserRole | null) ?? null;
      if (!role || !m.userId) groups.unregistered.push(m);
      else if (groups[role]) groups[role].push(m);
    });
    return groups;
  }, [markers]);

  const roleCounts = useMemo(() => {
    const c: Record<UserRole | 'unregistered', number> = {
      user: 0, tester: 0, tester_plus: 0, admin: 0, super_admin: 0, unregistered: 0,
    };
    for (const [role, items] of Object.entries(devicesByRole)) {
      c[role as UserRole | 'unregistered'] = items.length;
    }
    return c;
  }, [devicesByRole]);

  const toggleRole = (role: UserRole | 'unregistered') => {
    setRoleFilter((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const handleLocateMarker = useCallback((m: FleetMapMarker) => {
    if (m.lastKnownPosition && mapRef.current) {
      mapRef.current.flyTo(m.lastKnownPosition.lat, m.lastKnownPosition.lon, 14);
      setSelectedMarker(m);
    }
  }, []);

  const filtered = useMemo(() => {
    return markers.filter((m) => {
      const matchesSearch =
        !search ||
        m.userName?.toLowerCase().includes(search.toLowerCase()) ||
        m.userEmail?.toLowerCase().includes(search.toLowerCase()) ||
        m.deviceName?.toLowerCase().includes(search.toLowerCase()) ||
        m.deviceId.toLowerCase().includes(search.toLowerCase());

      const matchesStatus = statusFilter === 'all' || getPinColour(m) === statusFilter;

      const markerRole = (m.userRole as UserRole | null) ?? null;
      const isUnregistered = !m.userId || !markerRole;
      const matchesRole =
        roleFilter.size === 0 ||
        (isUnregistered ? roleFilter.has('unregistered') : roleFilter.has(markerRole as UserRole));

      return matchesSearch && matchesStatus && matchesRole;
    });
  }, [markers, search, statusFilter, roleFilter]);

  const statusCounts = useMemo(() => {
    const c: Record<PinColour, number> = { green: 0, amber: 0, red: 0, grey: 0 };
    markers.forEach((m) => c[getPinColour(m)]++);
    return c;
  }, [markers]);

  useEffect(() => { setSelectedMarker(null); }, [search, statusFilter, roleFilter]);

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('en-NZ')
    : null;

  const activeFiltersCount = (statusFilter !== 'all' ? 1 : 0) + roleFilter.size;

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-white">Fleet Map</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {filtered.length !== markers.length
              ? <><span className="text-white">{filtered.length}</span> of {markers.length} vessels shown</>
              : <>{markers.length} vessel{markers.length !== 1 ? 's' : ''} with known position{noGnssDevices.length > 0 && <span className="text-slate-500"> · {noGnssDevices.length} registered without GNSS</span>}</>
            }
            {lastUpdated && <span className="ml-2 text-slate-500">· updated {lastUpdated}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {activeFiltersCount > 0 && (
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
              onClick={() => { setStatusFilter('all'); setRoleFilter(new Set()); }}
            >
              <XMarkIcon className="w-3 h-3" />
              Clear filters ({activeFiltersCount})
            </button>
          )}
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
            onClick={() => refetch()}
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-2">
        {/* Row 1: search + status filter */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, email or device…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-red-500"
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            {(['all', 'green', 'amber', 'red', 'grey'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  statusFilter === f
                    ? 'bg-slate-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {f !== 'all' && (
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PIN_COLOURS[f] }} />
                )}
                {f === 'all' ? `All (${markers.length})` : statusCounts[f]}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: role dropdowns — z-[1100] to sit above Leaflet map */}
        <div className="relative z-[1100] flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 mr-1">Role:</span>
          {ALL_ROLES.map((role) => (
            <RoleDropdown
              key={role}
              label={ROLE_LABELS[role]}
              count={roleCounts[role]}
              active={roleFilter.has(role)}
              onToggle={() => toggleRole(role)}
              items={devicesByRole[role]}
              onSelect={handleLocateMarker}
            />
          ))}
          <RoleDropdown
            label="Unregistered"
            count={roleCounts.unregistered}
            active={roleFilter.has('unregistered')}
            onToggle={() => toggleRole('unregistered')}
            items={devicesByRole.unregistered}
            onSelect={handleLocateMarker}
          />
          <span className="w-px h-5 bg-slate-700 mx-1" />
          <NoGnssDropdown items={noGnssDevices} />
        </div>
      </div>

      {/* Map */}
      <div className="relative flex-1 min-h-[500px] rounded-xl overflow-hidden border border-slate-700">
        {isLoading && (
          <div className="absolute inset-0 z-[1001] flex items-center justify-center bg-slate-900/70">
            <div className="flex items-center gap-2 text-slate-300 text-sm">
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
              Loading fleet positions…
            </div>
          </div>
        )}

        {!isLoading && markers.length === 0 && (
          <div className="absolute inset-0 z-[1001] flex flex-col items-center justify-center bg-slate-900/80">
            <MapPinIcon className="w-10 h-10 text-slate-600 mb-2" />
            <p className="text-slate-400 text-sm">No vessels with known positions yet.</p>
            <p className="text-slate-500 text-xs mt-1">Positions are reported by active Scout devices.</p>
          </div>
        )}

        {!isLoading && markers.length > 0 && filtered.length === 0 && (
          <div className="absolute inset-0 z-[1001] flex flex-col items-center justify-center bg-slate-900/80">
            <MapPinIcon className="w-10 h-10 text-slate-600 mb-2" />
            <p className="text-slate-400 text-sm">No vessels match the current filters.</p>
            <button
              type="button"
              className="mt-2 text-xs text-red-400 hover:text-red-300"
              onClick={() => { setStatusFilter('all'); setRoleFilter(new Set()); setSearch(''); }}
            >
              Clear all filters
            </button>
          </div>
        )}

        <AdminFleetMap>
          <MapController ref={mapRef} />

          {filtered.map((m) => {
            if (!m.lastKnownPosition) return null;
            const colour = getPinColour(m);
            return (
              <Marker
                key={m.deviceId}
                position={[m.lastKnownPosition.lat, m.lastKnownPosition.lon]}
                icon={makeIcon(colour)}
                eventHandlers={{ click: () => setSelectedMarker(m) }}
              >
                <Popup>
                  <div className="text-xs leading-relaxed">
                    <p className="font-semibold">{m.userName || m.userEmail || 'Unregistered'}</p>
                    {m.userRole && <p className="text-slate-500">{ROLE_LABELS[m.userRole as UserRole] ?? m.userRole}</p>}
                    {m.deviceName && <p className="text-slate-500">{m.deviceName}</p>}
                    {m.packageName && <p>{m.packageName}</p>}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </AdminFleetMap>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 z-[1000] bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-lg px-3 py-2">
          <ul className="space-y-1">
            {LEGEND_ITEMS.map(({ colour, label }) => (
              <li key={colour} className="flex items-center gap-2 text-xs text-slate-300">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: PIN_COLOURS[colour] }}
                />
                {label}
              </li>
            ))}
          </ul>
        </div>

        {/* Selected vessel panel */}
        {selectedMarker && (
          <MarkerPanel marker={selectedMarker} onClose={() => setSelectedMarker(null)} />
        )}
      </div>
    </div>
  );
}

export default AdminFleetMapPage;
